import { dataLayer } from '@/constants';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { IProductDetails } from '@/interfaces/product';
import useHeaderStore from '@/store/header';
import { Figtree } from 'next/font/google';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import useFooterStore from '@/store/footer';

const Modal = dynamic(() => import('@/components/Modal'), { ssr: false });
const ProductDetailImage = dynamic(() => import('@/components/ProductDetailImage'), { ssr: false });
const CookieConsent = dynamic(() => import('./CookieConsent'), { ssr: false });

const figtree = Figtree({ subsets: ['latin'], weight: ['400', '500', '600', '700'], display: 'swap', fallback: ['"Segoe UI"', 'sans-serif'] });

const EmptyLayout = ({ children }: any) => <>{children}</>;

export default function AppContainer({ Component, pageProps }: any) {
  const Layout = Component.Layout || EmptyLayout;
  const router = useRouter();
  //This is required!
  const authObject = useReactQuerySession();
  //This is required!
  const activateOverlay = useHeaderStore(({ activateOverlay }) => activateOverlay);
  const { openQuickView, quickView, quickViewLoading, quickViewProduct, setQuickViewSeName, setQuickViewProduct } = useHeaderStore();
  const [productOptions, setProductOptions] = useState<any>(null);
  const { setShowFullFooter } = useFooterStore((store) => store);

  useEffect(() => {
    if (quickView && quickViewProduct) {
      let ProductOptions = dynamic(() => import('@/components/ProductOptions'));
      setProductOptions(
        <ProductOptions
          setProductCardSeName={setQuickViewSeName}
          quickView={true}
          product={quickViewProduct}
          productInfoGraphic={quickViewProduct?.ProductViewed}
        />,
      );
    }
  }, [quickViewProduct]);

  useEffect(() => {
    // lazyLoadImages();
    window.addEventListener('scroll', () => {
      if (window.scrollY > 10) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    });
    return () => {
      window.removeEventListener('scroll', () => {});
    };
  }, []);

  useEffect(() => {
    const routeChangeStart = (url: any, { shallow }: any) => {
      activateOverlay(false);
      openQuickView(false);
      setQuickViewProduct({} as IProductDetails);
      setQuickViewSeName('');
      // if (typeof window !== 'undefined') {
      //   window.document.body.scrollTo(0, 0);
      // }
    };
    router.events.on('routeChangeComplete', routeChangeStart);

    return () => {
      router.events.off('routeChangeComplete', routeChangeStart);
    };
  }, [router]);

  useEffect(() => {
    if (!router.asPath.includes('/category/') && !router.asPath.includes('/brand/')) {
      setShowFullFooter(true);
    } else {
      setShowFullFooter(false);
    }
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: 'page_view',
      ecommerce: {
        pagePath: router.asPath,
      },
    });
  }, [router.asPath]);

  return (
    <main className={'flex flex-col ' + figtree.className} id="app-main">
      <Layout params={Layout.params}>
        <Component {...pageProps}></Component>
        {typeof window !== 'undefined' && <CookieConsent />}
      </Layout>
      <div id="portal" />

      {quickView && (
        <Modal
          className="relative p-0"
          contentClass="w-full "
          open={quickView}
          heading="Quick View"
          closeModal={() => {
            openQuickView(false);
            setQuickViewProduct({} as IProductDetails);
            setQuickViewSeName('');
          }}
        >
          {quickViewLoading ? (
            <div>
              <div className="flex min-h-[548px] items-center lg:min-w-[1000px]">
                <div className="absolute top-0 bottom-0 right-0 left-0 z-[10000] flex h-full flex-col items-center justify-center p-5 ">
                  <p className=" mb-2 text-center text-3xl"> Quick view product is loading</p>
                  <button className="loading p-5"></button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {quickViewProduct ? (
                <div className=" gap-8 md:flex">
                  <div className="product-images w-full md:w-[600px] lg:w-[620px] xl:w-[630px]">
                    <ProductDetailImage quickView={true} productDetail={quickViewProduct} />
                  </div>
                  <div className="relative -mr-8 mt-5 w-[444px] overflow-y-auto pr-8 md:mt-0 md:w-[400px] lg:w-[420px] xl:w-[444px]">
                    {productOptions && productOptions}
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[548px] items-center lg:min-w-[1000px]">
                  <div className="absolute top-0 bottom-0 right-0 left-0 z-[10000] flex h-full flex-col items-center justify-center p-5 ">
                    <p className="text-center text-3xl">Product detail is not available.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </main>
  );
}
